import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const CartContext = createContext(null);

// Cart line: { id, productId, name, category, size, price, quantity }
export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [coupon, setCoupon] = useState(null);
  const [open, setOpen] = useState(false);

  const subtotal = useMemo(() => items.reduce((n, i) => n + i.price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((n, i) => n + i.quantity, 0), [items]);
  const discount = coupon ? Math.min(coupon.discount, subtotal) : 0;
  const total = Math.max(0, subtotal - discount);

  const lineId = (productId, size) => `${productId}::${size}`;

  const add = useCallback((product, variant, quantity = 1) => {
    const id = lineId(product.id, variant.size);
    setItems((prev) => {
      const found = prev.find((i) => i.id === id);
      if (found) return prev.map((i) => (i.id === id ? { ...i, quantity: i.quantity + quantity } : i));
      return [...prev, {
        id, productId: product.id, name: product.name, category: product.category,
        size: variant.size, price: variant.price, quantity,
      }];
    });
    toast.success("Added to cart", { description: `${product.name} · ${variant.size}` });
    setOpen(true);
  }, []);

  const updateQty = useCallback((id, delta) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, quantity: i.quantity + delta } : i)).filter((i) => i.quantity > 0));
  }, []);
  const setQty = useCallback((id, qty) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, quantity: Math.max(1, qty) } : i)));
  }, []);
  const remove = useCallback((id) => setItems((prev) => prev.filter((i) => i.id !== id)), []);
  const clear = useCallback(() => { setItems([]); setCoupon(null); }, []);

  // Re-validate coupon when subtotal changes
  useEffect(() => {
    if (!coupon) return;
    if (items.length === 0) { setCoupon(null); return; }
    (async () => {
      try {
        const r = await fetch(`${API}/api/coupon/validate`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: coupon.code, subtotal }),
        });
        if (!r.ok) { setCoupon(null); toast.info("Coupon removed", { description: "Cart total no longer meets the requirement." }); return; }
        const data = await r.json();
        setCoupon(data);
      } catch { /* keep */ }
    })();
  }, [subtotal]); // eslint-disable-line

  const applyCoupon = useCallback(async (code) => {
    if (!code) return { ok: false };
    if (items.length === 0) { toast.warning("Add items first"); return { ok: false }; }
    try {
      const res = await fetch(`${API}/api/coupon/validate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, subtotal }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error("Coupon not applied", { description: data.detail || "Try another code." }); return { ok: false }; }
      setCoupon(data);
      toast.success(`Coupon applied · ${data.code}`, { description: data.label || "" });
      return { ok: true, data };
    } catch { toast.error("Could not verify coupon"); return { ok: false }; }
  }, [items.length, subtotal]);

  const removeCoupon = useCallback(() => { setCoupon(null); toast.info("Coupon removed"); }, []);

  return (
    <CartContext.Provider value={{
      items, count, subtotal, discount, total, coupon,
      add, updateQty, setQty, remove, clear,
      applyCoupon, removeCoupon,
      open, setOpen,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
